import { useEffect, useState } from 'react'
import { BoardBadge } from '../components/lease/BoardBadge'
import { attorneysApi } from '../services/attorneys'

const PREPARED_MESSAGE =
  "A LeaseCheck user reviewed their lease and would like your help. Their analysis and lease are attached."

function Initials({ name }) {
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()
  return (
    <span className="flex h-14 w-14 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-base font-bold text-white">
      {initials}
    </span>
  )
}

function CardBody({ attorney, isYou }) {
  return (
    <>
      <div className="flex items-center gap-3">
        {attorney.photo_url ? (
          <img src={attorney.photo_url} alt="" className="h-14 w-14 flex-none rounded-full object-cover" />
        ) : (
          <Initials name={attorney.name} />
        )}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-800 dark:text-slate-100">{attorney.name}</span>
            {attorney.sponsored && (
              <span className="rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-2 py-0.5 text-xs font-semibold text-white">
                Featured
              </span>
            )}
            {isYou && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 ring-1 ring-amber-300">
                Your listing
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">{attorney.firm}</p>
          {attorney.board_member && (
            <span className="mt-1 inline-block">
              <BoardBadge tier={attorney.board_tier} member />
            </span>
          )}
        </div>
      </div>
      {attorney.bio && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{attorney.bio}</p>}
      {attorney.specialties?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {attorney.specialties.map((s) => (
            <span
              key={s}
              className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300"
            >
              {s}
            </span>
          ))}
        </div>
      )}
    </>
  )
}

function AttorneyCard({ attorney, selected, onSelect, readOnly, isYou }) {
  const ring = isYou
    ? 'ring-2 ring-amber-400 dark:ring-amber-400'
    : selected
      ? 'ring-2 ring-emerald-500 dark:ring-emerald-400'
      : 'ring-emerald-100 dark:ring-slate-700'
  const base = `w-full rounded-2xl bg-white p-4 text-left shadow-sm ring-1 transition dark:bg-slate-800/60 ${ring}`

  // Attorney preview: cards are read-only (no inquiry action).
  if (readOnly) {
    return (
      <div className={base}>
        <CardBody attorney={attorney} isYou={isYou} />
      </div>
    )
  }
  return (
    <button type="button" onClick={() => onSelect(attorney.id)} className={base}>
      <CardBody attorney={attorney} isYou={isYou} />
    </button>
  )
}

export function Directory({ documentId, viewerMode, viewerAttorneyId }) {
  const isAttorneyView = viewerMode === 'attorney'
  const [attorneys, setAttorneys] = useState(null)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: PREPARED_MESSAGE })
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [sentTo, setSentTo] = useState(null)

  useEffect(() => {
    attorneysApi.list().then(setAttorneys).catch((e) => setError(e.message))
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSending(true)
    setSendError(null)
    try {
      const result = await attorneysApi.submitInquiry({
        ...form,
        attorney_id: selectedId,
        document_id: documentId,
      })
      setSentTo(result.attorney)
    } catch (err) {
      setSendError(err.message)
    } finally {
      setSending(false)
    }
  }

  if (sentTo) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">Message sent!</h1>
        <p className="mt-4 text-slate-600 dark:text-slate-300">
          Your message is on its way to {sentTo.name} at {sentTo.firm}.
        </p>
      </main>
    )
  }

  if (error) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center text-slate-600 dark:text-slate-300">
        Could not load attorneys. Try again shortly.
      </main>
    )
  }

  if (!attorneys) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center text-slate-600 dark:text-slate-300">
        Loading attorneys…
      </main>
    )
  }

  const featured = attorneys.filter((a) => a.sponsored)
  const standard = attorneys.filter((a) => !a.sponsored)
  const canSubmit = selectedId && form.name && form.email

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <header className="text-center">
        <h1 className="text-4xl font-bold text-slate-800 dark:text-slate-100">LeaseCheck Attorney Directory</h1>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          {isAttorneyView
            ? 'This is the live directory as renters see it. Your listing is highlighted below.'
            : 'Pick a Colorado attorney to review your lease. Your LeaseCheck analysis and lease will be attached to your message.'}
        </p>
      </header>

      {attorneys.length === 0 && (
        <p className="mt-8 text-center text-slate-600 dark:text-slate-300">
          No attorneys are listed yet. Please check back soon.
        </p>
      )}

      {featured.length > 0 && (
        <section className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Featured Attorneys</h2>
          <div className="mt-4 space-y-3">
            {featured.map((a) => (
              <AttorneyCard
                key={a.id}
                attorney={a}
                selected={selectedId === a.id}
                onSelect={setSelectedId}
                readOnly={isAttorneyView}
                isYou={isAttorneyView && a.id === viewerAttorneyId}
              />
            ))}
          </div>
        </section>
      )}

      {standard.length > 0 && (
        <section className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">All Attorneys</h2>
          <div className="mt-4 space-y-3">
            {standard.map((a) => (
              <AttorneyCard
                key={a.id}
                attorney={a}
                selected={selectedId === a.id}
                onSelect={setSelectedId}
                readOnly={isAttorneyView}
                isYou={isAttorneyView && a.id === viewerAttorneyId}
              />
            ))}
          </div>
        </section>
      )}

      {!isAttorneyView && selectedId && (
        <section className="mt-8">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Contact this firm</h2>
          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <input
              type="text"
              placeholder="Your Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border-0 bg-white p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
            />
            <input
              type="email"
              placeholder="Your Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-xl border-0 bg-white p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
            />
            <input
              type="tel"
              placeholder="Your Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full rounded-xl border-0 bg-white p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
            />
            <textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={4}
              className="w-full rounded-xl border-0 bg-white p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
            />
            {sendError && <p className="text-center text-sm text-rose-600 dark:text-rose-400">{sendError}</p>}
            <button
              type="submit"
              disabled={!canSubmit || sending}
              className="w-full rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 font-semibold text-white shadow-sm disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send'}
            </button>
          </form>
        </section>
      )}

      <footer className="mt-10 text-center text-xs text-slate-500 dark:text-slate-400">
        This directory is provided for convenience. LeaseCheck does not endorse any attorney's services.
        Featured listings receive priority placement.
      </footer>
    </main>
  )
}

export default Directory
